<template>
  <transition name="search-nav-slide-down">
    <div
      v-if="shouldShowSearch"
      ref="search"
      class="additional-nav__block site-search"
      @mouseenter="$store.dispatch('nav/setAdditionalNav', 'search')"
    >
      <div
        class="site-search__inner-container"
        :class="[
          { 'site-search__inner-container--open': showInnerContainer },
          `site-search__inner-container--large-input`,
        ]"
      >
        <span class="icon-cross site-search__icon-cross" @click="closeSearchOverlay" />
        <Debounced-search-box
          autocomplete="new-password"
          autocorrect="off"
          autocapitalize="off"
          class="site-search__input"
          name="search_input"
          ref="search_input"
          :placeholder="$t('search')"
          :value="localQuery"
          @stopped="refine"
          :refocus="refocusSearch"
        />
        <div class="site-search__border"></div>
        <div class="site-search__wrapper">
          <LoadingIndicator v-if="isLoading" class="site-search__loader" />
          <div v-show="isIntroActive" class="site-search__intro">
            <span class="site-search__intro-header">{{ $t('recent_searches') }}:</span>
            <ul>
              <li class="site-search__intro-item" @click="refine('Cloudswift')">Cloudswift</li>
              <li class="site-search__intro-item" @click="refine('Waterproof')">Waterproof</li>
              <li class="site-search__intro-item" @click="refine('Long-T')">Long-T</li>
            </ul>
          </div>
          <div
            v-show="hasResults && !isLoading"
            class="site-search__products"
            ref="productsContainer"
          >
            <div class="site-search__status-bar">
              <div class="site-search__status-bar-results">
                {{ items.length }} {{ $t('results') }}
              </div>
              <a
                class="site-search__status-bar-link u-arrow-link"
                @click="followPageLink('explore')"
                >{{ $t('shop_all_products') }}</a
              >
            </div>
            <Cards
              :cards="items"
              :context="$CONTEXTS.search"
              :isQuickViewEnabled="false"
              :summaryIsHidden="true"
              @card-click="trackCardClick"
            />
          </div>

          <div v-show="isNoResultsShown" class="site-search__no-results">
            <p class="site-search__no-results-description">
              {{ $t('no_results_msg', { term: localQuery }) }}
            </p>
            <div class="site-search__no-results-button-group">
              <button class="site-search__no-results-button" @click="followPageLink('shoe-finder')">
                {{ $t('global.go_to_shoe_finder') }}
              </button>
              <button class="site-search__no-results-button" @click="followPageLink('explore')">
                {{ $t('shop_all_products') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import { getProductsInProductImpressionFormat } from '@/helpers/product'
import { createWidgetMixin } from 'vue-instantsearch'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import { productsIndex, promoCardsIndex } from './Search.vue'
import DebouncedSearchBox from './DebouncedSearchBox.vue'
import { connectStateResults } from './searchConnector'

export default {
  name: 'Search',
  components: {
    DebouncedSearchBox,
    LoadingIndicator,
    Cards: () => import(/* webpackChunkName: "cards" */ '@/components/_shared/Cards/Cards.vue'),
  },
  mixins: [createWidgetMixin({ connector: connectStateResults })],

  data() {
    return {
      localQuery: '',
      showInnerContainer: false,
      productsIndex,
      promoCardsIndex,
    }
  },

  computed: {
    ...mapGetters('nav', ['shouldShowSearch', 'refocusSearch']),

    hasQuery() {
      return this.state?.query?.length > 0
    },

    isIntroActive() {
      return !this.hasQuery
    },

    items() {
      return this.state?.items || []
    },

    hasResults() {
      return this.state?.hasResults
    },

    isLoading() {
      return this.state?.isSearchStalled
    },

    // This prop is forwarded to the connector by instantSearch
    widgetParams() {
      const { country, language } = this.$store.state.page
      return { country, language }
    },

    isNoResultsShown() {
      return !this.hasResults && !this.isLoading && this.hasQuery
    },
  },

  watch: {
    introIsActive() {
      if (!this.introIsActive) On.destroyMobileScrollHelper(this.$refs.search)
    },

    shouldShowSearch() {
      if (this.shouldShowSearch) {
        window.addEventListener('touchstart', this.blurInputField)
        window.dispatchEvent(new CustomEvent('on:search-open'))
        this.$nextTick(() => {
          this.showInnerContainer = true
          On.initMobileScrollHelper(this.$refs.search)
          On.initMobileScrollHelper(this.$refs.productsContainer)
        })
      } else {
        window.removeEventListener('touchstart', this.blurInputField)
        On.destroyMobileScrollHelper(this.$refs.productsContainer)
        this.showInnerContainer = false
      }
    },

    'state.items'(results = []) {
      const { query } = this.state?.state || {}
      if (query) {
        this.reportToGtm({ results, query })
      }
    },
  },

  methods: {
    closeSearchOverlay() {
      this.$store.dispatch('nav/setAdditionalNav', '')
      this.$store.commit('nav/setOverlayState', false)
    },

    refine(value) {
      this.localQuery = value
      this.state.refine(value)
    },

    trackCardClick(card) {
      this.state.sendEvent('click', card, 'Search result clicked')
    },

    reportToGtm({ results, query }) {
      notifyExternalServices({
        action: ACTIONS.virtPath,
        details: {
          virtPagePath: `/search?q=${query}`,
          virtPageTitle: 'Internal Search',
          searchResults: String(results.length),
          isNavigating: false,
        },
      })

      const productResults = results.filter(i => !i.isPromoCard)

      if (productResults.length) {
        notifyExternalServices({
          action: ACTIONS.listView,
          productData: {
            items: getProductsInProductImpressionFormat(productResults, this.$CONTEXTS.search),
          },
        })
      }
    },

    followPageLink(page) {
      this.$store.dispatch('nav/setAdditionalNav', '')
      window.location.href = `/${this.$store.state.page.locale}/${page}`
    },

    hideSearch() {
      this.$store.dispatch('nav/setAdditionalNav', '')
    },

    blurInputField() {
      this.$refs.search_input?.blur()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Search.scss';
</style>

<i18n src="@/locales/site_search.json">
</i18n>
